import React from 'react';
import type { FormInstance } from 'antd';
import { ProductMultiSelectField, ProductQuantityRows } from '../components';
import type {
  ProductMultiSelectFieldProps,
  SelectedProduct,
} from '../components/ProductMultiSelectField';
import { applyProductQuantityDelta } from '../shellShipmentHelpers';
import { ALL_PRODUCTS } from './boxProducts';
import { indexOfKey, type LineBox, type PackageLineValues } from './lines';
import styles from './styles.module.css';

export type ProductSelectShared = Omit<
  ProductMultiSelectFieldProps,
  'selectedProducts' | 'onProductsChange' | 'label' | 'fieldName' | 'ariaLabel'
>;

export interface LineProductsFieldsProps {
  /** Position, for labels only; the line is addressed by `lineKey`. */
  lineIndex: number;
  lineKey: string;
  /** Boxes in the line. */
  qty: number;
  /** The line's products with quantity per box; may hold the `all_products` sentinel. Kept by the owner per line key, not in the form. */
  products: SelectedProduct[];
  onChange: (products: SelectedProduct[]) => void;
  select: ProductSelectShared;
}

/** Drops the products' serials from every box of the line, so re-adding one starts blank. */
const clearSerials = (
  form: FormInstance,
  lineKey: string,
  productIds: string[]
): void => {
  // Resolved now, not at render: an All Products fetch can finish after a line above was removed.
  const lines: PackageLineValues[] = form.getFieldValue('lines') ?? [];
  const lineIndex = indexOfKey(lines, lineKey);
  if (lineIndex < 0) return;
  const boxes: Array<LineBox | undefined> | undefined = form.getFieldValue([
    'lines',
    lineIndex,
    'boxes',
  ]);
  const holds = (b?: LineBox): boolean => {
    const serials = b?.serials;
    return (
      !!serials &&
      typeof serials === 'object' &&
      productIds.some((id) => id in serials)
    );
  };
  if (!boxes?.some(holds)) return;
  form.setFieldValue(
    ['lines', lineIndex, 'boxes'],
    boxes.map((b) =>
      holds(b)
        ? {
            ...b,
            serials: Object.fromEntries(
              Object.entries(b?.serials ?? {}).filter(
                ([id]) => !productIds.includes(id)
              )
            ),
          }
        : b
    )
  );
};

const LineProductsFields: React.FC<LineProductsFieldsProps> = ({
  lineIndex,
  lineKey,
  qty,
  products,
  onChange,
  select,
}) => {
  const multiBox = qty > 1;
  const hasProducts = products.some((p) => p.id !== ALL_PRODUCTS);

  // Every path that drops a product (select tag, option toggle, row delete) also drops its serials.
  const changeProducts = (next: SelectedProduct[]): void => {
    onChange(next);
    const kept = new Set(next.map((p) => p.id));
    const removed = products
      .filter((p) => p.id !== ALL_PRODUCTS && !kept.has(p.id))
      .map((p) => p.id);
    if (removed.length > 0) clearSerials(select.form, lineKey, removed);
  };

  return (
    <>
      <ProductMultiSelectField
        {...select}
        label={
          multiBox ? 'Products in each box (optional)' : 'Products (optional)'
        }
        ariaLabel={`Products in line ${lineIndex + 1}`}
        fieldName={null}
        selectedProducts={products}
        onProductsChange={changeProducts}
      />
      {hasProducts && (
        <>
          <div className={styles.boxHint}>
            {multiBox ? 'Items per box' : 'Items in this box'}
          </div>
          <ProductQuantityRows
            selectedProducts={products}
            setSelectedProducts={changeProducts}
            showSerials={false}
            getProductName={select.getProductName}
            updateQuantity={(id, delta) => {
              const { next, error } = applyProductQuantityDelta(
                products,
                id,
                delta
              );
              if (!error) onChange(next);
            }}
            removeProduct={(id) =>
              changeProducts(
                products.filter((p) => p.id !== id && p.id !== ALL_PRODUCTS)
              )
            }
          />
        </>
      )}
      {hasProducts && multiBox && (
        <div className={styles.lineNote}>
          These products and quantities apply to every box in this line. Serial
          numbers stay per box.
        </div>
      )}
    </>
  );
};

export default LineProductsFields;
